import React from 'react';
import { styled } from '@compiled/react';
import DynamicTable from '@atlaskit/dynamic-table';
import Heading from '@atlaskit/heading';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { Deployment } from '../../common/types';
import { TABLE_HEIGHT } from '../../common/ui/per-deployment-detail-table/constants';
import { getWeekRangeFromWeekStartDate } from '../../common/utils/date-range';
import { useSelectedWeekStore } from '../../controllers/selected-date-range';
import { useSelectedDeploymentId } from '../../controllers/selected-deployment';
import { ViewDeploymentFrequencyReportExperienceSuccessTracker } from '../../services/experience-tracker';
import { usePeriodBreakdownMetrics } from '../../services/period-breakdown';
import { LoadingSkeleton } from './loading-skeleton';
import messages from './messages';
import { useTableHeader, useTableRows } from './utils';

const Title = () => {
	const { formatMessage } = useIntl();

	return (
		<TableTitleContainer>
			<Heading size="xsmall">{formatMessage(messages.chartTitle)}</Heading>
		</TableTitleContainer>
	);
};

export const PerDeploymentDetailTable = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ selectedWeekStart }] = useSelectedWeekStore();
	const [, { setSelectedDeploymentId }] = useSelectedDeploymentId();
	const { data, loading, error } = usePeriodBreakdownMetrics(
		selectedWeekStart !== null ? getWeekRangeFromWeekStartDate(selectedWeekStart) : null,
	);

	const selectDeployment = (deployment: Deployment) => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'issuesPerDeploymentCell',
			}),
		);
		setSelectedDeploymentId(deployment.id);
	};

	const rows = useTableRows(
		data?.perDeploymentMetrics,
		data?.medianIssuesPerDeployment,
		selectDeployment,
	);
	const header = useTableHeader();

	if ((error && rows === null) || loading || rows === null || selectedWeekStart === null) {
		return <LoadingSkeleton />;
	}
	if (error) {
		// TODO - We would want to fire analytics? Normal data exists but error exist together?!
	}

	return (
		<IssueTableContainer data-test-id="software-deployment-frequency-report.ui.per-deployment-detail-table">
			<ViewDeploymentFrequencyReportExperienceSuccessTracker />
			<Title />
			<DynamicTable
				// This is to forcibly trigger re-rendering when selectedWeekStart is changed. Some weeks have 5 pages,
				// some weeks have less pages than 5, which will make table stay in a wrong page if we don't do this
				key={JSON.stringify(selectedWeekStart)}
				head={header}
				rows={rows}
				rowsPerPage={!loading && rows.length > 10 ? 10 : undefined}
				defaultPage={1}
				loadingSpinnerSize="large"
				isLoading={loading}
				isFixedSize
				defaultSortKey="jiraIssuesPerDeployment"
				defaultSortOrder="DESC"
			/>
		</IssueTableContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableTitleContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 2}px`,
	margin: `0 0 ${token('space.200', '16px')} 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueTableContainer = styled.div<{ faded?: boolean }>({
	padding: `0 ${token('space.100', '8px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: ({ faded }) => (faded ? 0.5 : 1),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${TABLE_HEIGHT}px`,
});
